import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2, Upload, XCircle } from "lucide-react";
import api from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Logo } from "../../components/Logo";
import { Card, Button, Spinner } from "../../components/ui-kit";

// Stessa lista/ordine di backend/routers_client_docs.py (CLIENT_DOC_FIELDS):
// solo la firma è obbligatoria, tutto il resto è facoltativo e non blocca
// l'invio — chi manca viene comunque segnalato al consulente in dashboard.
const FIELD_INFO = {
  signature: { hint: "Fotografa la tua firma su un foglio bianco, ben illuminata e senza ombre. Formato JPEG o PNG.", accept: "image/jpeg,image/png" },
  id_document: { hint: "Fronte e retro in un'unica foto o PDF, se possibile.", accept: "image/jpeg,image/png,application/pdf" },
  tax_code_card: { hint: "Tessera sanitaria o altro documento con il codice fiscale.", accept: "image/jpeg,image/png,application/pdf" },
  chamber_certificate: { hint: "Visura camerale aggiornata dell'azienda.", accept: "image/jpeg,image/png,application/pdf" },
  floor_plan: { hint: "Planimetria del locale/sede.", accept: "image/jpeg,image/png,application/pdf" },
  technical_report: { hint: "Relazione tecnica, se disponibile.", accept: "image/jpeg,image/png,application/pdf" },
};

export default function ClientDocsForm() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(() => {
    api.get(`/public/client-docs/${token}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (fieldKey, file) => {
    if (!file) return;
    setUploading(fieldKey);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/public/client-docs/${token}/upload?field_key=${encodeURIComponent(fieldKey)}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Caricamento non riuscito. Riprova.");
    } finally {
      setUploading(null);
      if (inputRefs.current[fieldKey]) inputRefs.current[fieldKey].value = "";
    }
  };

  const remove = async (fieldKey, label) => {
    if (!window.confirm(`Rimuovere "${label}"? Dovrai ricaricarlo per inviarlo di nuovo.`)) return;
    setUploading(fieldKey);
    try {
      await api.delete(`/public/client-docs/${token}/${fieldKey}`);
      toast.success("Documento rimosso");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile rimuovere il documento.");
    } finally { setUploading(null); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/public/client-docs/${token}/submit`);
      setSubmitted(res);
      toast.success("Modulo inviato al tuo consulente");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio non riuscito. Riprova.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-haccp-bg flex flex-col items-center p-6">
      <div className="w-full max-w-lg">
        <div className="flex justify-center my-8"><Logo size="md" /></div>
        {data === undefined ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : data === null ? (
          <Card className="p-8 text-center" data-testid="client-docs-invalid">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold">Link non valido</h1>
            <p className="text-slate-500 font-body mt-2">Il link è scaduto o non è più attivo. Chiedi al tuo consulente di inviartene uno nuovo.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden" data-testid="client-docs-form">
            <div className="bg-navy text-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-bright">Documenti azienda</div>
              <h1 className="font-heading text-2xl font-bold mt-1">{data.tenant_name}</h1>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500">Carica qui i documenti richiesti dal tuo consulente. <strong>Solo la firma è obbligatoria</strong>: puoi inviare il modulo anche se gli altri documenti non sono ancora pronti, e tornare qui in seguito ad aggiungerli.</p>
              {data.fields.map((field) => {
                const info = FIELD_INFO[field.key] || {};
                const busy = uploading === field.key;
                return (
                  <div key={field.key} className={`rounded-lg border p-4 ${field.present ? "border-emerald-200 bg-emerald-50/50" : field.required ? "border-red-200 bg-red-50/50" : "border-hairline"}`} data-testid={`client-doc-field-${field.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-medium text-navy">
                          {field.label}
                          {field.required && <span className="text-[10px] uppercase tracking-wide text-red-600 font-semibold">Obbligatoria</span>}
                        </div>
                        {info.hint && <p className="text-xs text-slate-500 mt-0.5">{info.hint}</p>}
                        {field.present ? (
                          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`client-doc-present-${field.key}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> Caricato{field.uploaded_at ? ` il ${formatItalianDateTime(field.uploaded_at)}` : ""}
                          </div>
                        ) : (
                          <div className="mt-1.5 text-xs text-slate-400">Non ancora caricato</div>
                        )}
                      </div>
                      {field.present && (
                        <button type="button" onClick={() => remove(field.key, field.label)} disabled={busy} className="shrink-0 grid h-9 w-9 place-items-center rounded-lg text-red-600 hover:bg-red-100 disabled:opacity-50" aria-label={`Rimuovi ${field.label}`} data-testid={`client-doc-remove-${field.key}`}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                    <input ref={(el) => { inputRefs.current[field.key] = el; }} type="file" accept={info.accept} className="hidden" id={`file-${field.key}`}
                      onChange={(e) => upload(field.key, e.target.files?.[0])} />
                    <label htmlFor={`file-${field.key}`} className={`mt-3 inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${busy ? "opacity-50 pointer-events-none" : "hover:bg-slate-50"}`} data-testid={`client-doc-upload-${field.key}`}>
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {field.present ? "Sostituisci file" : "Carica file"}
                    </label>
                  </div>
                );
              })}
              {submitted ? (
                <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-4 text-emerald-800 text-sm" data-testid="client-docs-submitted">
                  <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Modulo inviato al tuo consulente.</div>
                  {submitted.missing_fields?.length > 0 && (
                    <p className="mt-1.5">Puoi comunque tornare qui in qualsiasi momento per aggiungere: {submitted.missing_fields.map((k) => (FIELD_INFO[k] ? data.fields.find((f) => f.key === k)?.label : k)).filter(Boolean).join(", ")}.</p>
                  )}
                </div>
              ) : (
                <Button variant="success" className="w-full" loading={submitting}
                  disabled={!data.fields.find((f) => f.key === "signature")?.present}
                  onClick={submit} data-testid="client-docs-submit">
                  Invia modulo al consulente
                </Button>
              )}
              {!data.fields.find((f) => f.key === "signature")?.present && !submitted && (
                <p className="text-xs text-center text-slate-400">Carica la firma per poter inviare il modulo.</p>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
